// ============================================
// pages/gunny-bags/GunnyReconciliationReport.tsx
// Reconciles PO totals against actual allotment activity, broken down per
// physical godown. Pool rows carry PO bags and on-hand; godown rows carry
// activity + capacity throughput.
// ============================================
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Download, Loader2, Scale } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/markfed';
import { reportsAPI, seasonsAPI, districtsAPI, GunnyReconciliationRow } from '../../api/services';
import type { Season, District } from '../../types/markfed';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

export const GunnyReconciliationReport: React.FC = () => {
  const { user, hasRole } = useAuth();
  const isDm = user?.role === UserRole.DM;

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [seasonId, setSeasonId] = useState<number>(0);
  const [districtId, setDistrictId] = useState<number>(0);
  const [rows, setRows] = useState<GunnyReconciliationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([seasonsAPI.list(), districtsAPI.list()]);
        const sList = Array.isArray(s.data) ? s.data : [];
        setSeasons(sList);
        setDistricts(Array.isArray(d.data) ? d.data : []);
        const active = sList.find((x) => x.is_active) || sList[0];
        if (active) setSeasonId(active.id);
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load filters');
      }
    })();
  }, []);

  useEffect(() => {
    if (!seasonId) return;
    setLoading(true);
    setErr(null);
    reportsAPI
      .gunnyReconciliation({
        season_id: seasonId,
        district_id: districtId || undefined,
      })
      .then((res) => setRows(res.data.rows))
      .catch((e) => setErr(e?.response?.data?.message || 'Failed to load reconciliation'))
      .finally(() => setLoading(false));
  }, [seasonId, districtId]);

  // Totals across pool rows only
  const totals = useMemo(() => {
    return rows.filter((r) => r.is_pool).reduce(
      (acc, r) => {
        acc.po_bags += r.po_bags;
        acc.allotted += r.allotted;
        acc.utilized += r.utilized;
        acc.returned += r.returned;
        acc.on_hand += r.on_hand ?? 0;
        acc.po_count += r.po_count;
        acc.allotment_count += r.allotment_count;
        return acc;
      },
      { po_bags: 0, allotted: 0, utilized: 0, returned: 0, on_hand: 0, po_count: 0, allotment_count: 0 },
    );
  }, [rows]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(rows, 50);

  const downloadXlsx = async () => {
    if (!seasonId) return;
    setDownloading(true);
    try {
      const res = await reportsAPI.gunnyReconciliationXlsx({
        season_id: seasonId,
        district_id: districtId || undefined,
      });
      const blob = new Blob([res.data as any], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gunny-reconciliation-season${seasonId}${districtId ? `-district${districtId}` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const onHandFlag = (r: GunnyReconciliationRow) => {
    if (!r.is_pool || !r.po_bags || r.on_hand == null) return '';
    const pct = (r.on_hand / r.po_bags) * 100;
    if (pct > 50) return 'bg-red-50 text-red-800';
    if (pct > 20) return 'bg-amber-50 text-amber-800';
    return '';
  };

  const capUseFlag = (r: GunnyReconciliationRow) => {
    if (r.capacity_use_pct == null) return '';
    if (r.capacity_use_pct > 100) return 'bg-red-50 text-red-800';
    if (r.capacity_use_pct > 80) return 'bg-amber-50 text-amber-800';
    return '';
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Scale className="w-7 h-7 text-amber-600" />
            Gunny Reconciliation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pool rows (italic) show PO inflow vs on-hand at the district level.
            Sub-rows show per-godown activity and capacity throughput.
          </p>
        </div>
        <button
          onClick={downloadXlsx}
          disabled={downloading || !seasonId}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download XLSX
        </button>
      </div>

      {err && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <label className="text-sm font-medium text-gray-700">Season</label>
        <select value={seasonId} onChange={(e) => setSeasonId(Number(e.target.value))}
          className="px-2 py-1.5 text-sm border rounded bg-white">
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{(s as any).season_name} {s.is_active ? '(active)' : ''}</option>
          ))}
        </select>

        {!isDm && (
          <>
            <label className="text-sm font-medium text-gray-700 ml-3">District</label>
            <select value={districtId} onChange={(e) => setDistrictId(Number(e.target.value))}
              className="px-2 py-1.5 text-sm border rounded bg-white">
              <option value={0}>All districts</option>
              {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </>
        )}

        {hasRole(UserRole.SUPER_ADMIN, UserRole.MD, UserRole.MANAGER_PROCUREMENT) && (
          <span className="ml-auto text-xs text-gray-500">
            Pools: <span className="font-medium">{rows.filter((r) => r.is_pool).length}</span>
            {' · '}Godown rows: <span className="font-medium">{rows.filter((r) => !r.is_pool).length}</span>
            {totals.on_hand > 0 && <> · Total on-hand: <span className="font-semibold">{totals.on_hand.toLocaleString()}</span> bags</>}
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            No reconciliation data yet — no manager-approved POs or allotments in this scope.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">District</th>
                  <th className="px-3 py-2">Supplier</th>
                  <th className="px-3 py-2">Godown / Pool</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Cap MT</th>
                  <th className="px-3 py-2 text-right">POs</th>
                  <th className="px-3 py-2 text-right">PO Bags</th>
                  <th className="px-3 py-2 text-right">Allotted</th>
                  <th className="px-3 py-2 text-right">Utilized</th>
                  <th className="px-3 py-2 text-right">Returned</th>
                  <th className="px-3 py-2 text-right">On-hand</th>
                  <th className="px-3 py-2 text-right">Cap use %</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((r, idx) => (
                  <tr
                    key={`${r.supplier_id}-${r.district_id}-${r.godown_id ?? 'pool'}-${idx}`}
                    className={`border-b last:border-0 hover:bg-gray-50 ${r.is_pool ? 'bg-gray-50/60 font-medium' : ''}`}
                  >
                    <td className="px-3 py-2 text-gray-500">{r.sl_no}</td>
                    <td className="px-3 py-2">{r.district_name}</td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{r.supplier_name}</td>
                    <td className={`px-3 py-2 ${r.is_pool ? 'italic' : ''}`}>
                      {r.is_pool ? (
                        <span className="text-gray-700">[District pool]</span>
                      ) : (
                        <span className="pl-4 text-gray-700">{r.godown_name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {r.godown_type ? r.godown_type.toUpperCase() : (r.is_pool ? '' : '—')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-600">
                      {r.capacity_mts ? r.capacity_mts.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.po_count || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.po_bags > 0 ? r.po_bags.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.allotted.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.utilized.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.returned.toLocaleString()}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${onHandFlag(r)}`}>
                      {r.on_hand == null ? '—' : r.on_hand.toLocaleString()}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${capUseFlag(r)}`}>
                      {r.capacity_use_pct == null ? '—' : `${r.capacity_use_pct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t">
                <tr className="font-semibold">
                  <td colSpan={6} className="px-3 py-2 text-right">TOTAL (pools only)</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.po_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.po_bags.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.allotted.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.utilized.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.returned.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals.on_hand.toLocaleString()}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="rows"
            />
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-500">
        <span className="inline-block w-3 h-3 bg-amber-50 border border-amber-200 align-middle mr-1" /> &gt; 20% on-hand · &gt; 80% capacity
        <span className="inline-block w-3 h-3 bg-red-50 border border-red-200 align-middle ml-3 mr-1" /> &gt; 50% on-hand · &gt; 100% capacity
        &nbsp;— flags for ops follow-up. Godown on-hand shown as "—" until PO→godown delivery splits are tracked; godown Cap-use% reflects throughput (allotted × 50&nbsp;kg / capacity).
      </p>
    </div>
  );
};

export default GunnyReconciliationReport;
